import React, { useEffect, useMemo, useState } from 'react';
import EmbeddedCheckoutComponent from '../EmbeddedCheckout';
import OrderSummary from '../OrderSummary';
import { plans } from '../../data/plans';
import { getSubscriptionPlans, SubscriptionPlan } from '../../services/credits';
import Portal from '../Portal';

interface StripeCheckoutModalProps {
  open: boolean;
  onClose: () => void;
  clientSecret: string | null;
  customerId: string | null;
  planId: string | null;
  billingPeriod: 'monthly' | 'annual';
  publishableKey?: string | null;
  userId?: string;
  onComplete?: () => void;
  onError?: (error: Error) => void;
}

export default function StripeCheckoutModal({
  open,
  onClose,
  clientSecret,
  customerId,
  planId,
  billingPeriod,
  publishableKey,
  userId,
  onComplete,
  onError,
}: StripeCheckoutModalProps) {
  const [stripePlans, setStripePlans] = useState<Record<string, SubscriptionPlan>>({});

  // Fetch Stripe plan prices on mount
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const response = await getSubscriptionPlans();
        const plansMap: Record<string, SubscriptionPlan> = {};
        response.plans.forEach((plan) => {
          plansMap[plan.id] = plan;
        });
        setStripePlans(plansMap);
      } catch (err) {
        console.error('Failed to fetch Stripe plans:', err);
        // Continue with default plans if fetch fails
      }
    };
    fetchPlans();
  }, []);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [open]);

  // Handle ESC key to close
  useEffect(() => {
    if (!open) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [open, onClose]);

  // Handle backdrop click
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Get plan data for order summary
  const planData = useMemo(() => {
    if (!planId) return null;
    return plans.find(p => p.id === planId);
  }, [planId]);

  // Calculate pricing from Stripe data
  const orderSummaryData = useMemo(() => {
    if (!planData || !planId) return null;

    const stripePlan = stripePlans[planId];
    if (!stripePlan) return null;

    const monthlyPrice = stripePlan.price_monthly_usd;
    const annualPrice = stripePlan.price_annual_usd;
    
    const totalAmount = billingPeriod === 'annual' ? annualPrice : monthlyPrice;
    const monthlyEquivalent = billingPeriod === 'annual' ? annualPrice / 12 : undefined;

    return {
      planName: planData.name,
      totalAmount,
      monthlyEquivalent,
    };
  }, [planData, planId, stripePlans, billingPeriod]);

  if (!open) return null;

  return (
    <Portal>
      <div
        className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[9999] overflow-y-auto"
        onClick={handleBackdropClick}
      >
        <div className="min-h-screen flex items-center justify-center p-4">
        <div className="w-full max-w-4xl my-auto">
        <div className="bg-bg border border-white/20 rounded-xl p-4 sm:p-6 md:p-8 shadow-lg">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl sm:text-2xl font-bold text-white">Complete Your Subscription</h2>
            <button
              onClick={onClose}
              className="text-white/70 hover:text-white text-2xl font-bold transition-colors flex-shrink-0 w-8 h-8 flex items-center justify-center"
              aria-label="Close"
            >
              ×
            </button>
          </div>

          {/* Stripe Checkout Content */}
          <div className="max-h-[80vh] overflow-y-auto">
            {/* Order Summary */}
            {orderSummaryData && (
              <OrderSummary
                planName={orderSummaryData.planName}
                billingCadence={billingPeriod}
                amount={orderSummaryData.totalAmount}
                monthlyEquivalent={orderSummaryData.monthlyEquivalent}
              />
            )}

            {/* Stripe Embedded Checkout */}
            {clientSecret && customerId && planId && userId ? (
              <EmbeddedCheckoutComponent
                clientSecret={clientSecret}
                customerId={customerId}
                planId={planId}
                billingPeriod={billingPeriod}
                publishableKey={publishableKey || undefined}
                userId={userId}
                onComplete={onComplete}
                onError={onError}
              />
            ) : (
              <div className="text-white/70 text-center py-8">
                Loading checkout...
              </div>
            )}
          </div>
        </div>
      </div>
      </div>
      </div>
    </Portal>
  );
}

